import { ChangeDetectorRef, DestroyRef, Directive, ElementRef, EventEmitter, input, Output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Rive } from '@rive-app/canvas';
import { BehaviorSubject, map, Subject, switchMap, tap } from 'rxjs';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rive]',
  standalone: true,
})
export class RiveDirective {
  constructor(
    private readonly el: ElementRef<HTMLCanvasElement>,
    private readonly cdRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {
    // Handle play state changes
    this.isPlaying$
      .pipe(
        switchMap((isPlaying) => this.instance$.pipe(map((instance) => ({ instance, isPlaying })))),
        tap(({ instance, isPlaying }) => {
          if (instance) {
            if (isPlaying) {
              instance.play();
            } else {
              instance.pause();
            }
            this.cdRef.markForCheck();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Initialize and handle riveFile changes
    toObservable(this.riveFile)
      .pipe(
        switchMap(() => {
          // Clean up existing instance
          this.instance$$.value?.cleanup();
          // Initialize new instance with updated file
          return this.initializeRive();
        }),
        tap((instance) => {
          this.instance$$.next(instance);
          this.riveLoaded.emit(instance);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // // Handle mouse hover
    // fromEvent(this.el.nativeElement, 'mouseenter')
    //   .pipe(
    //     tap(() => {
    //       this.setPlaying(false);
    //       this.cdRef.markForCheck();
    //     }),
    //     takeUntilDestroyed(this.destroyRef),
    //   )
    //   .subscribe();

    // fromEvent(this.el.nativeElement, 'mouseleave')
    //   .pipe(
    //     tap(() => {
    //       this.setPlaying(true);
    //       this.cdRef.markForCheck();
    //     }),
    //     takeUntilDestroyed(this.destroyRef),
    //   )
    //   .subscribe();
  }

  @Output() riveLoaded = new EventEmitter<Rive>();

  riveInstance!: Rive;

  readonly riveFile = input.required<string>();
  readonly artboard = input<string>();
  readonly stateMachine = input<string>();
  readonly autoplay = input(true);

  private readonly instance$$ = new BehaviorSubject<Rive | undefined>(undefined);
  readonly instance$ = this.instance$$.asObservable();

  private readonly isPlaying$$ = new BehaviorSubject<boolean>(true);
  readonly isPlaying$ = this.isPlaying$$.asObservable();

  private readonly speed$$ = new BehaviorSubject<number>(1);
  readonly speed$ = this.speed$$.asObservable();

  private readonly stateTrigger$$ = new Subject<string>();
  readonly stateTrigger$ = this.stateTrigger$$.asObservable();

  private readonly animationComplete$$ = new Subject<void>();
  readonly animationComplete$ = this.animationComplete$$.asObservable();

  // Public API
  setPlaying(isPlaying: boolean) {
    this.isPlaying$$.next(isPlaying);
  }

  setSpeed(speed: number) {
    this.speed$$.next(speed);
  }

  restart() {
    const instance = this.instance$$.value;
    if (instance) {
      instance.stop();
      instance.play();
      this.cdRef.markForCheck();
    }
  }

  triggerState(stateName: string) {
    this.stateTrigger$$.next(stateName);
  }

  get isPlaying() {
    return this.isPlaying$$.value;
  }

  get speed() {
    return this.speed$$.value;
  }

  // Private methods

  private async initializeRive(): Promise<Rive | undefined> {
    try {
      this.riveInstance = new Rive({
        canvas: this.el.nativeElement,
        src: this.riveFile(),
        autoplay: this.autoplay(),
        artboard: this.artboard(),
        stateMachines: this.stateMachine(),
        useOffscreenRenderer: true,
        onLoad: () => {
          // this.riveInstance.resizeDrawingSurfaceToCanvas();
          this.riveLoaded.emit(this.riveInstance);
          if (this.autoplay()) {
            this.instance$$.value?.play();
            this.cdRef.markForCheck();
          } else {
            this.instance$$.value?.pause();
            this.cdRef.markForCheck();
          }
        },
        onStateChange: (event) => {
          const eventData = event.data as string[];
          if (eventData[0]?.includes('Exit')) {
            this.animationComplete$$.next();
            this.cdRef.markForCheck();
          }
        },
      });

      return this.riveInstance;
    } catch (error) {
      console.error('Error initializing Rive:', error);
      return undefined;
    }
  }
}
